import { CalendarToday } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import clsx from "clsx";
import { useMemo, type FC, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { useRtl } from "@ryde/redux";
import {
  getComputedGridFieldName,
  onActivateKeyDown,
  RideFormFields,
  rideStartEndPositionAtom,
  useFormats,
  useHasPermissions,
  type ICustomField,
  type TRideDtoStartEndKey,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { useDecorateWithMark } from "../../utils";
import { type IRenderColumnTemplate } from "./_core";

export const RideStartEndDateEditableTemplate: FC<IRenderColumnTemplate> = ({
  value: initialValue,
  rowId,
  name,
  isColumn,
  gridAttributes,
}) => {
  const { decorateValue } = useDecorateWithMark();
  const [rideStartEndPosition, setRideStartEndPosition] = useRecoilState(
    rideStartEndPositionAtom,
  );
  const { formatDateTimeDelimited, isAMPM } = useFormats();
  const { isRtl } = useRtl();
  const { hasPermissions } = useHasPermissions({
    entity: "ride",
    action: "update",
  });

  const rideDtoDateFieldKey = useMemo(
    () =>
      getComputedGridFieldName({
        name,
        isColumn,
        gridAttributes,
      } as ICustomField) as TRideDtoStartEndKey,
    [gridAttributes, isColumn, name],
  );

  const { t } = useTranslation();
  const startOrEndText =
    rideDtoDateFieldKey === RideFormFields.rideStart
      ? t("ride.START")
      : t("ride.END");
  const isHidden =
    rideStartEndPosition.isFieldOpened &&
    rideStartEndPosition.rideId === rowId &&
    rideStartEndPosition.fieldName === rideDtoDateFieldKey;

  const highlightedValue = useMemo(() => {
    const formattedValue = formatDateTimeDelimited(initialValue);
    return decorateValue(formattedValue);
  }, [initialValue, formatDateTimeDelimited, decorateValue]);

  const onClickHandler = useEvent((event: MouseEvent<HTMLSpanElement>) => {
    if (hasPermissions) {
      const target = event.currentTarget as HTMLSpanElement;
      const rect = target?.getBoundingClientRect();

      const { x, y } = rect ?? {};

      if (!rowId) {
        return;
      }

      setRideStartEndPosition(prev => ({
        ...prev,
        isFieldOpened: true,
        iconAnchorElement: null,
        x,
        y,
        rideId: rowId as number,
        fieldName: rideDtoDateFieldKey,
      }));
    }
  });

  const onIconClickHandler = useEvent(event => {
    if (hasPermissions) {
      setRideStartEndPosition(prev => ({
        ...prev,
        isFieldOpened: false,
        iconAnchorElement: event.currentTarget,
        rideId: rowId as number,
        fieldName: rideDtoDateFieldKey,
      }));
    }
  });

  return (
    <span
      className={clsx("ride-start-end-date-editable-template", {
        "ride-start-end-date-editable-template--hidden": isHidden,
        "ride-start-end-date-editable-template--ampm": isAMPM,
      })}
    >
      <span
        className="ride-start-end-date-editable-template__date"
        onClick={onClickHandler}
        onKeyDown={onActivateKeyDown<HTMLSpanElement>(event =>
          onClickHandler(event as unknown as MouseEvent<HTMLSpanElement>),
        )}
        role="button"
        tabIndex={0}
      >
        <FramerTooltip
          content={t("ride.RIDE_DATETIME_TOOLTIP", {
            startOrEnd: startOrEndText,
          })}
        >
          <span dir={isRtl ? "rtl" : "ltr"}>{highlightedValue}</span>
        </FramerTooltip>
      </span>

      <IconButton
        onClick={onIconClickHandler}
        size="small"
        disabled={!hasPermissions}
      >
        <FramerTooltip
          content={t("ride.RIDE_DATETIME_EDIT_TOOLTIP", {
            startOrEnd: startOrEndText,
          })}
        >
          <CalendarToday sx={{ width: "15px", height: "15px" }} />
        </FramerTooltip>
      </IconButton>
    </span>
  );
};
